import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface PlanInfo {
  plan: 'free' | 'pro' | 'custom';
  isPro: boolean;
  isFree: boolean;
  isCustom: boolean;
  subscriptionEnd: string | null;
  loading: boolean;
  limits: {
    maxGalleries: number | null; // null = unlimited
    maxImagesPerGallery: number | null;
  };
  refresh: () => void;
}

export function usePlan(): PlanInfo {
  const { session } = useAuth();
  const [plan, setPlan] = useState<'free' | 'pro' | 'custom'>('free');
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchPlan = useCallback(async () => {
    if (!session) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (!error && data) {
        setPlan(data.plan || 'free');
        setSubscriptionEnd(data.subscriptionEnd || null);
      }
    } catch (e) {
      console.error('Error checking plan:', e);
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    if (!session) return;
    const interval = setInterval(fetchPlan, 60000);
    return () => clearInterval(interval);
  }, [session, fetchPlan]);

  // Refresh on upgrade=success query param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgrade') === 'success') {
      // Remove query param
      const url = new URL(window.location.href);
      url.searchParams.delete('upgrade');
      window.history.replaceState({}, '', url.toString());
      // Refresh after a delay to let webhook process
      setTimeout(fetchPlan, 2000);
    }
  }, [fetchPlan]);

  const isPro = plan === 'pro' || plan === 'custom';

  return {
    plan,
    isPro,
    isFree: plan === 'free',
    isCustom: plan === 'custom',
    subscriptionEnd,
    loading,
    limits: {
      maxGalleries: isPro ? null : 5,
      maxImagesPerGallery: isPro ? null : 10,
    },
    refresh: fetchPlan,
  };
}
